import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchCurrentUserGroupIds, fetchGroupMembers } from './graph';

// Group id configured via environment variable
const configuredGroupId = process.env.REACT_APP_SCIA_USERS_GROUP || '';

const GroupContext = createContext({
  allowed: false,
  groupId: configuredGroupId,
  members: [],
  loading: true,
  needsConsent: false,
  requestConsent: async () => {},
});

export const GroupProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [members, setMembers] = useState([]);
  const [needsConsent, setNeedsConsent] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function init() {
      setLoading(true);
      setNeedsConsent(false);
      try {
        const account = accounts && accounts.length ? accounts[0] : null;
        if (!instance || !account) {
          if (mounted) {
            setAllowed(false);
            // setMembers([]);
            setLoading(false);
          }
          return;
        }

        // Get current user's group ids
        const userGroupIds = await fetchCurrentUserGroupIds(instance, account);
        const isAllowed = Array.isArray(userGroupIds) && userGroupIds.includes(configuredGroupId);

        if (isAllowed) {
          // Fetch members for the configured group id
          // const groupMembers = await fetchGroupMembers(instance, account, configuredGroupId);
          if (mounted) {
            setAllowed(true);
            // setMembers(groupMembers || []);
          }
        } else {
          if (mounted) {
            setAllowed(false);
            // setMembers([]);
          }
        }
      } catch (err) {
        console.error('GroupProvider init error:', err);
        // If the underlying graph helper threw an 'interaction_required' error, flag that consent/interaction is required
        if (err && err.message === 'interaction_required') {
          if (mounted) setNeedsConsent(true);
        }
        if (mounted) {
          setAllowed(false);
          // setMembers([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();
    return () => { mounted = false; };
  }, [instance, accounts]);

  // requestConsent triggers a redirect-based interactive token acquisition for the scopes we need
  const requestConsent = async () => {
    const account = accounts && accounts.length ? accounts[0] : null;
    if (!instance || !account) return;
    const scopes = ["User.Read", "Directory.Read.All", "Group.Read.All"];
    try {
      // Use redirect so popups are not required and the browser will navigate for user consent
      await instance.acquireTokenRedirect({ account, scopes });
    } catch (err) {
      console.error('requestConsent error:', err);
      // If redirect fails, set needsConsent so UI can show guidance
      setNeedsConsent(true);
    }
  };

  return (
    <GroupContext.Provider value={{ allowed, groupId: configuredGroupId, loading, needsConsent, requestConsent }}>
      {children}
    </GroupContext.Provider>
  );
};

export const useGroup = () => useContext(GroupContext);

export default GroupContext;